import {
  AlertTriangle,
  CheckCircle2,
  Download,
  FileSpreadsheet,
  GitMerge,
  Replace,
  Upload,
  X,
} from 'lucide-react';
import { useDeferredValue, useEffect, useId, useMemo, useRef, useState } from 'react';

import { cn } from '@/lib/utils';

import {
  CASCADE_NODE_WARN_THRESHOLD,
  type Cascade,
  MAX_CASCADE_LEVELS,
} from '../elements/fieldConfig';
import {
  type CascadeSheetOutcome,
  mergeCascade,
  parseCascadeSheet,
  sampleCascadeCsv,
} from '../lib/cascadeSheet';

/**
 * Bulk-loads a dropdown's options from a spreadsheet.
 *
 * WHY THIS EXISTS. The cascade editor is fine for a dozen options and hopeless for a real list —
 * nobody types 36 states and 750 districts into a nested tree, one input at a time. Everything
 * needed is already in a sheet somewhere, so the job is to read that sheet, not to make typing
 * faster.
 *
 * PASTE IS THE PRIMARY PATH, not file upload. Excel and Sheets put tab-separated text on the
 * clipboard, so Ctrl+C over a range and Ctrl+V here is the shortest route from "the data I have" to
 * "the data in the form" — no export step, no file to find, and nothing leaves the browser. The file
 * picker is offered for a CSV somebody was sent.
 *
 * NOTHING IS APPLIED WITHOUT A PREVIEW. An import that silently overwrites a tree someone spent an
 * hour building is the one thing that would make this feature hated, so the counts and every
 * problem found are shown first, and committing is a separate, named choice — Replace or Merge.
 */
export function CascadeImportDialog({
  open,
  allow,
  existing,
  onCancel,
  onApplyCascade,
  onApplyOptions,
}: {
  open: boolean;
  /**
   * Which shapes this caller can accept.
   *
   * `both` is the normal case for a Drop Down that has no cascade yet: THE SHEET DECIDES — one
   * column becomes a plain option list, two or more become dependent dropdowns. That is the whole
   * reason this is one dialog and not two.
   *
   * `options` for Single / Multiple Choice, which have no dependent-dropdown mode at all.
   * `cascade` once a cascade exists, where a single column would be a step backwards rather than an
   * import.
   *
   * A shape the caller cannot take is refused with an explanation, never by silently dropping
   * columns.
   */
  allow: 'cascade' | 'options' | 'both';
  /** The cascade being imported into, for Merge and for the sample's width. Null when there is none. */
  existing: Cascade | null;
  onCancel: () => void;
  onApplyCascade?: (cascade: Cascade) => void;
  onApplyOptions?: (options: string[]) => void;
}) {
  const [text, setText] = useState('');
  const [fileNote, setFileNote] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaId = useId();

  // Cleared on open so a second import never starts with the first one's rows still in the box.
  useEffect(() => {
    if (open) {
      setText('');
      setFileNote(null);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    document.addEventListener('keydown', onKey);
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
    };
  }, [open, onCancel]);

  /**
   * Parsing follows the DEFERRED text, not the live value.
   *
   * A 20,000-row paste parses in tens of milliseconds, which is fine once and not fine on every
   * keystroke if somebody then edits a header in the box. `useDeferredValue` lets React keep typing
   * responsive and re-parse when it can, with no timer to tune or clean up.
   */
  const deferredText = useDeferredValue(text);
  const outcome = useMemo<CascadeSheetOutcome | null>(
    () => (deferredText.trim().length === 0 ? null : parseCascadeSheet(deferredText)),
    [deferredText],
  );

  if (!open) return null;

  /**
   * A usable result IN THIS MODE.
   *
   * A sheet can parse perfectly and still be the wrong shape for the caller. Rather than take the
   * first column of a four-column sheet — which would drop three columns of someone's data without
   * saying so — the mismatch is reported as its own message.
   */
  const mismatch =
    outcome === null || outcome.kind === 'error'
      ? null
      : allow === 'cascade' && outcome.kind === 'options'
        ? 'This sheet has one column, so it describes a single dropdown with nothing depending on it. This field already has dependent levels — add one column per level, with the header row naming them.'
        : allow === 'options' && outcome.kind === 'cascade'
          ? `This sheet has ${outcome.cascade.levels.length} columns, which describes ${outcome.cascade.levels.length} dependent dropdowns. This question takes one list of options, so it needs a single column — dependent dropdowns are a Drop Down field.`
          : null;

  const errorMessage =
    outcome?.kind === 'error' ? outcome.message : mismatch !== null ? mismatch : null;

  const readyCascade = mismatch === null && outcome?.kind === 'cascade' ? outcome : null;
  const readyOptions = mismatch === null && outcome?.kind === 'options' ? outcome : null;
  const canApply = readyCascade !== null || readyOptions !== null;

  /** Merge needs something to merge into, and only makes sense for a cascade. */
  const canMerge = readyCascade !== null && existing !== null && existing.nodes.length > 0;

  /**
   * Downloads one of the two samples.
   *
   * TWO SAMPLES, NAMED, rather than one guessed from the mode. In `both` mode the field could become
   * either shape, and picking one silently is how somebody downloads a three-column sheet for a
   * plain dropdown and turns it into a cascade by accident — or downloads a one-column sheet when
   * they wanted levels, which is exactly the confusion this replaced.
   */
  function downloadSample(kind: 'options' | 'cascade') {
    // Real values in both, for the same reason: `Option 1 / Option 2` shows the shape but nothing
    // about what belongs in it.
    const csv =
      kind === 'options'
        ? ['Branch', 'Ahmedabad', 'Vadodara', 'Surat', 'Pune', 'Mumbai'].join('\r\n')
        : sampleCascadeCsv(existing?.levels ?? []);

    // A BOM so Excel opens a UTF-8 CSV with accented option names intact rather than as mojibake.
    const blob = new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = kind === 'options' ? 'options-sample.csv' : 'dependent-dropdowns-sample.csv';
    link.click();
    // Revoked on the next tick — revoking synchronously can cancel the download in some browsers.
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }

  async function readFile(file: File) {
    setFileNote(null);

    // A binary workbook is not text, and reading it would fill the box with unreadable bytes. Say
    // what to do instead — this is the single most likely wrong move a user makes here.
    if (/\.(xlsx|xls|xlsm|ods|numbers)$/i.test(file.name)) {
      setFileNote(
        `“${file.name}” is a spreadsheet file, not a sheet of text. In Excel use File → Save As → CSV, or select the cells, copy them, and paste into the box above.`,
      );
      return;
    }

    const MAX_FILE_BYTES = 5 * 1024 * 1024;
    if (file.size > MAX_FILE_BYTES) {
      setFileNote(
        `“${file.name}” is ${(file.size / 1024 / 1024).toFixed(1)} MB. The most that can be read is 5 MB.`,
      );
      return;
    }

    try {
      setText(await file.text());
      setFileNote(`Loaded “${file.name}”.`);
    } catch {
      setFileNote(`“${file.name}” could not be read. Try copying the cells and pasting instead.`);
    }
  }

  function apply(action: 'replace' | 'merge') {
    if (readyOptions !== null) {
      onApplyOptions?.(readyOptions.options);
      return;
    }
    if (readyCascade === null) return;

    const next =
      action === 'merge' && existing !== null
        ? mergeCascade(existing, readyCascade.cascade)
        : readyCascade.cascade;

    onApplyCascade?.(next);
  }

  const heading =
    allow === 'options'
      ? 'Import options'
      : allow === 'cascade'
        ? 'Import dropdown levels'
        : 'Import from a sheet';

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40 p-0 backdrop-blur-sm sm:p-4">
      {/* Full-screen on a phone, a centred sheet from `sm` up — a fixed-size modal is what breaks on
          a small screen, where the footer buttons fall off the bottom. `h-dvh` accounts for the
          mobile browser's own chrome, which `h-screen` does not. */}
      <div className="flex h-dvh w-full flex-col overflow-hidden rounded-none border-gray-100 bg-white shadow-2xl sm:h-auto sm:max-h-[90dvh] sm:max-w-2xl sm:rounded-2xl sm:border">
        <header className="flex shrink-0 items-center gap-3 border-b border-[#E5E7EB] px-5 py-4">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10">
            <FileSpreadsheet className="h-4 w-4 text-primary" />
          </span>
          <h3 className="min-w-0 flex-1 truncate text-base font-semibold text-black-logo">
            {heading}
          </h3>
          <button
            type="button"
            onClick={onCancel}
            aria-label="Close"
            title="Close"
            className="shrink-0 rounded-lg p-1.5 text-[#64748B] transition-colors hover:bg-[#F1F5F9] hover:text-destructive"
          >
            <X size={18} />
          </button>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          <FormatHint allow={allow} onDownloadSample={downloadSample} />

          <label
            htmlFor={textareaId}
            className="mt-4 mb-1.5 block text-[length:var(--text-14)] text-black"
          >
            Paste rows, or load a CSV
          </label>
          <textarea
            id={textareaId}
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={7}
            spellCheck={false}
            // In `both` mode the cascade shape is shown, since that is the one whose layout is not
            // self-evident; the format hint above states the one-column rule right next to it.
            placeholder={
              allow === 'options'
                ? 'Branch\nAhmedabad\nVadodara\nSurat'
                : 'Country,State,District\nIndia,Gujarat,Ahmedabad\n,,Vadodara\n,Maharashtra,Pune'
            }
            // Monospace so columns line up in the box, which is how a person spots a stray comma.
            className="w-full resize-y rounded-lg border border-placeholder bg-white px-3 py-2.5 font-mono text-[length:var(--text-13)] text-[#0F172A] outline-none transition-all placeholder:text-[#94A3B8] focus:border-primary focus:ring-1 focus:ring-primary/20"
          />

          <div className="mt-2 flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="flex items-center gap-1.5 rounded-lg border border-grayEF px-3 py-1.5 text-[length:var(--text-13)] font-medium text-[#374151] transition-colors hover:bg-[#F9FAFB]"
            >
              <Upload size={14} />
              Choose a CSV file
            </button>
            {text.length > 0 && (
              <button
                type="button"
                onClick={() => {
                  setText('');
                  setFileNote(null);
                }}
                className="flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-[length:var(--text-13)] font-medium text-[#64748B] transition-colors hover:text-destructive"
              >
                <X size={14} />
                Clear
              </button>
            )}
            <input
              ref={fileInputRef}
              type="file"
              // `.xlsx` is accepted by the picker ON PURPOSE, so choosing one produces the
              // explanation in `readFile` instead of the file being greyed out with no reason given.
              accept=".csv,.tsv,.txt,.xlsx,.xls,text/csv,text/plain"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) void readFile(file);
                // Reset so choosing the SAME file twice fires a change event the second time.
                e.target.value = '';
              }}
            />
          </div>

          {fileNote !== null && (
            <p className="mt-2 text-[length:var(--text-12)] leading-relaxed text-[#64748B]">
              {fileNote}
            </p>
          )}

          {errorMessage !== null && <Callout tone="error" message={errorMessage} />}

          {readyCascade !== null && (
            <Preview
              summary={`${readyCascade.cascade.levels.length} levels: ${readyCascade.cascade.levels
                .map((level) => level.title)
                .join(' → ')}`}
              detail={readyCascade.cascade.levels
                .map(
                  (level, index) =>
                    `${(readyCascade.perLevel[index] ?? 0).toLocaleString()} ${level.title}`,
                )
                .join(' · ')}
              rowsUsed={readyCascade.rowsUsed}
              total={readyCascade.total}
              warnings={readyCascade.warnings}
            />
          )}

          {readyOptions !== null && (
            <Preview
              summary={`${readyOptions.options.length.toLocaleString()} options`}
              detail={readyOptions.options.slice(0, 6).join(' · ')}
              rowsUsed={readyOptions.rowsUsed}
              total={readyOptions.options.length}
              warnings={readyOptions.warnings}
            />
          )}
        </div>

        <footer className="flex shrink-0 flex-wrap items-center justify-end gap-2 border-t border-[#E5E7EB] px-5 py-4">
          <button
            type="button"
            onClick={onCancel}
            className="flex items-center gap-1.5 rounded-lg border border-grayEF px-4 py-2 text-[length:var(--text-14)] font-medium text-[#374151] transition-colors hover:bg-[#F9FAFB]"
          >
            <X size={15} />
            Cancel
          </button>

          {canMerge && (
            <button
              type="button"
              onClick={() => apply('merge')}
              title="Add these options to the ones already there"
              className="flex items-center gap-1.5 rounded-lg border border-primary px-4 py-2 text-[length:var(--text-14)] font-medium text-primary transition-colors hover:bg-primary/5"
            >
              <GitMerge size={15} />
              Merge
            </button>
          )}

          <button
            type="button"
            onClick={() => apply('replace')}
            disabled={!canApply}
            title={
              canMerge
                ? 'Discard the current options and use only the imported ones'
                : 'Use the imported options'
            }
            className="flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-[length:var(--text-14)] font-medium text-white transition-colors hover:bg-[#0038CC] disabled:opacity-50"
          >
            <Replace size={15} />
            {canMerge ? 'Replace all' : 'Import'}
          </button>
        </footer>
      </div>
    </div>
  );
}

/**
 * How the sheet has to look.
 *
 * Shown UP FRONT rather than behind a help link, because the format is the only thing a first-time
 * user needs to know and it fits in a few lines. The inherited-blank rule gets its own sentence: it
 * is the part nobody guesses, and the part that lets a sheet be imported exactly as it was typed.
 *
 * In `both` mode it states the rule that makes one dialog work for two shapes — the column count
 * decides — and offers each sample by name.
 */
function FormatHint({
  allow,
  onDownloadSample,
}: {
  allow: 'cascade' | 'options' | 'both';
  onDownloadSample: (kind: 'options' | 'cascade') => void;
}) {
  const showOptions = allow === 'options' || allow === 'both';
  const showCascade = allow === 'cascade' || allow === 'both';

  return (
    <div className="rounded-lg border border-[#E2E8F0] bg-[#F8FAFC] p-3">
      {allow === 'both' && (
        <p className="m-0 mb-2 text-[length:var(--text-12)] leading-relaxed text-[#475569]">
          <strong className="font-semibold text-[#0F172A]">
            The number of columns decides what you get.
          </strong>{' '}
          One column becomes a single dropdown. Two or more become dependent dropdowns, one per
          column ({MAX_CASCADE_LEVELS} is the maximum).
        </p>
      )}

      {showOptions && (
        <div className={allow === 'both' ? 'mt-2' : undefined}>
          <p className="m-0 text-[length:var(--text-12)] leading-relaxed text-[#475569]">
            <strong className="font-semibold text-[#0F172A]">One dropdown</strong> — one column. The
            first row is a heading; every row under it is an option.
          </p>
          <pre className="mt-1.5 mb-0 overflow-x-auto rounded border border-[#E2E8F0] bg-white px-2.5 py-2 font-mono text-[11px] leading-relaxed text-[#334155]">
            {'Branch\nAhmedabad\nVadodara\nSurat'}
          </pre>
          <button
            type="button"
            onClick={() => onDownloadSample('options')}
            className="mt-1.5 flex items-center gap-1.5 text-[length:var(--text-12)] font-semibold text-primary transition-colors hover:underline"
          >
            <Download size={13} strokeWidth={2.5} />
            Download this sample
          </button>
        </div>
      )}

      {showCascade && (
        <div className={showOptions ? 'mt-3 border-t border-[#E2E8F0] pt-3' : undefined}>
          <p className="m-0 text-[length:var(--text-12)] leading-relaxed text-[#475569]">
            <strong className="font-semibold text-[#0F172A]">Dependent dropdowns</strong> — one
            column per dropdown, one row per path. The first row names them. Leave a cell blank to
            repeat the value above it.
          </p>
          <pre className="mt-1.5 mb-0 overflow-x-auto rounded border border-[#E2E8F0] bg-white px-2.5 py-2 font-mono text-[11px] leading-relaxed text-[#334155]">
            {'Country,State,District\nIndia,Gujarat,Ahmedabad\n,,Vadodara\n,Maharashtra,Pune'}
          </pre>
          <button
            type="button"
            onClick={() => onDownloadSample('cascade')}
            className="mt-1.5 flex items-center gap-1.5 text-[length:var(--text-12)] font-semibold text-primary transition-colors hover:underline"
          >
            <Download size={13} strokeWidth={2.5} />
            Download this sample
          </button>
        </div>
      )}
    </div>
  );
}

/** What the sheet turned into, before anything is committed. */
function Preview({
  summary,
  detail,
  rowsUsed,
  total,
  warnings,
}: {
  summary: string;
  detail: string;
  rowsUsed: number;
  total: number;
  warnings: readonly { row: number | null; message: string }[];
}) {
  const rowWarnings = warnings.filter((w) => w.row !== null);
  const sheetWarnings = warnings.filter((w) => w.row === null);

  return (
    <div className="mt-3 flex flex-col gap-2">
      <div className="flex items-start gap-2 rounded-lg border border-[#BBF7D0] bg-[#F0FDF4] p-3">
        <CheckCircle2 size={16} className="mt-0.5 shrink-0 text-[#16A34A]" />
        <div className="min-w-0">
          <p className="m-0 text-[length:var(--text-13)] font-semibold break-words text-[#14532D]">
            {summary}
          </p>
          <p className="m-0 mt-0.5 text-[length:var(--text-12)] break-words text-[#166534]">
            {detail}
          </p>
          <p className="m-0 mt-0.5 text-[length:var(--text-12)] text-[#166534]">
            {rowsUsed.toLocaleString()} {rowsUsed === 1 ? 'row' : 'rows'} read ·{' '}
            {total.toLocaleString()} options in total
          </p>
        </div>
      </div>

      {/* The size note, and the reason it is a note rather than a block: nothing here is invalid,
          but the whole tree travels to every applicant's phone with the step. */}
      {total > CASCADE_NODE_WARN_THRESHOLD && (
        <Callout
          tone="warn"
          message={`${total.toLocaleString()} options is a large list. It is stored with the field and downloaded by every applicant when they reach this step, so on a slow connection they will wait for it. Consider splitting the deepest level into its own question.`}
        />
      )}

      {sheetWarnings.map((warning) => (
        <Callout key={warning.message} tone="warn" message={warning.message} />
      ))}

      {rowWarnings.length > 0 && (
        <div className="rounded-lg border border-[#FDE68A] bg-[#FFFBEB] p-3">
          <p className="m-0 flex items-center gap-1.5 text-[length:var(--text-12)] font-semibold text-[#92400E]">
            <AlertTriangle size={13} />
            {rowWarnings.length} {rowWarnings.length === 1 ? 'row needs' : 'rows need'} attention
          </p>
          <ul className="m-0 mt-1.5 flex list-none flex-col gap-0.5 p-0">
            {rowWarnings.map((warning) => (
              <li
                key={`${warning.row}-${warning.message}`}
                className="text-[length:var(--text-12)] break-words text-[#92400E]"
              >
                Row {warning.row} {warning.message}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function Callout({ tone, message }: { tone: 'error' | 'warn'; message: string }) {
  return (
    <div
      className={cn(
        'mt-3 flex items-start gap-2 rounded-lg border p-3 text-[length:var(--text-12)] leading-relaxed',
        tone === 'error'
          ? 'border-[#FECACA] bg-[#FEF2F2] text-[#991B1B]'
          : 'border-[#FDE68A] bg-[#FFFBEB] text-[#92400E]',
      )}
    >
      <AlertTriangle size={14} className="mt-0.5 shrink-0" />
      <span className="min-w-0 break-words">{message}</span>
    </div>
  );
}
